/* eslint camelcase: "off" */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

type DateFormat =
  | "yyyy/mm/dd"
  | "yyyy-mm-dd"
  | "dd/mm/yyyy"
  | "dd-mm-yyyy"
  | "yyyy/mm/dd hh:mm:ss"
  | "yyyy-mm-dd hh:mm:ss"
  | "dd/mm/yyyy hh:mm:ss"
  | "dd-mm-yyyy hh:mm:ss";

type Str2DateMap = {
  // eslint-disable-next-line
  [key in DateFormat]: (dateString: string) => Date;
};

type Date2StrMap = {
  // eslint-disable-next-line
  [key in DateFormat]: (date: Date) => string;
};

const str2DateYYYYMMDD = (dateString: string): Date => {
  const [year, month, day] = splitDate(dateString, "/");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const str2DateYYYYMMDD_Dash = (dateString: string): Date => {
  const [year, month, day] = splitDate(dateString, "-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const str2DateDDMMYYYY = (dateString: string): Date => {
  const [day, month, year] = splitDate(dateString, "/");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const str2DateDDMMYYYY_Dash = (dateString: string): Date => {
  const [day, month, year] = splitDate(dateString, "-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const Date2StrYYYYMMDD = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  return `${year}/${month}/${day}`;
};

const Date2StrYYYYMMDD_Dash = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  return `${year}-${month}-${day}`;
};

const Date2StrDDMMYYYY = (date: Date): string => {
  return date.toLocaleDateString();
};

const Date2StrDDMMYYYY_Dash = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  return `${day}-${month}-${year}`;
};

const getDateElements = (date: Date): [string, string, string] => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();
  return [day, month, year];
};

const splitDate = (dateString: string, separator: "-" | "/"): string[] => {
  return dateString.split(separator);
};

const str2str2DateYYYYMMDDDateTime_Dash = (dateString: string): Date => {
  const [datePart, timePart] = dateString.split(" ");
  const [year, month, day] = splitDate(datePart, "-");
  const [hours, minutes, seconds] = timePart.split(":").map(Number);
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  date.setHours(hours, minutes, seconds);
  return date;
};

const str2DateDDMMYYYYDateTime_Dash = (dateString: string): Date => {
  const [datePart, timePart] = dateString.split(" ");
  const [day, month, year] = splitDate(datePart, "-");
  const [hours, minutes, seconds] = timePart.split(":").map(Number);
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  date.setHours(hours, minutes, seconds);
  return date;
};

const str2str2DateYYYYMMDDDateTime = (dateString: string): Date => {
  const [datePart, timePart] = dateString.split(" ");
  const [year, month, day] = splitDate(datePart, "/");
  const [hours, minutes, seconds] = timePart.split(":").map(Number);
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  date.setHours(hours, minutes, seconds);
  return date;
};

const str2DateDDMMYYYYDateTime = (dateString: string): Date => {
  const [datePart, timePart] = dateString.split(" ");
  const [day, month, year] = splitDate(datePart, "/");
  const [hours, minutes, seconds] = timePart.split(":").map(Number);
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  date.setHours(hours, minutes, seconds);
  return date;
};

const Date2StrDDMMYYYYDateTime = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  const [hours, minutes, seconds] = getTimeElements(date);
  const datePart = `${day}/${month}/${year}`;
  return `${datePart} ${hours}:${minutes}:${seconds}`;
};

const Date2StrYYYYMMDDDateTime = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  const [hours, minutes, seconds] = getTimeElements(date);
  const datePart = `${year}/${month}/${day}`;
  return `${datePart} ${hours}:${minutes}:${seconds}`;
};

const Date2StrDDMMYYYYDateTime_Dash = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  const [hours, minutes, seconds] = getTimeElements(date);
  const datePart = `${day}-${month}-${year}`;
  return `${datePart} ${hours}:${minutes}:${seconds}`;
};

const Date2StrYYYYMMDDDateTime_Dash = (date: Date): string => {
  const [day, month, year] = getDateElements(date);
  const [hours, minutes, seconds] = getTimeElements(date);
  const datePart = `${year}-${month}-${day}`;
  return `${datePart} ${hours}:${minutes}:${seconds}`;
};

const getTimeElements = (date: Date): [string, string, string] => {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  return [hours, minutes, seconds];
};

const formatDate = (
  date: string | Date,
  inputFormat?: DateFormat,
  outputFormat?: DateFormat
): string | Date => {
  let result: string | Date = date;

  const str2DateMap: Str2DateMap = {
    "yyyy/mm/dd": str2DateYYYYMMDD,
    "yyyy-mm-dd": str2DateYYYYMMDD_Dash,
    "dd/mm/yyyy": str2DateDDMMYYYY,
    "dd-mm-yyyy": str2DateDDMMYYYY_Dash,
    "yyyy/mm/dd hh:mm:ss": str2str2DateYYYYMMDDDateTime,
    "yyyy-mm-dd hh:mm:ss": str2str2DateYYYYMMDDDateTime_Dash,
    "dd/mm/yyyy hh:mm:ss": str2DateDDMMYYYYDateTime,
    "dd-mm-yyyy hh:mm:ss": str2DateDDMMYYYYDateTime_Dash,
  };

  const date2StrMap: Date2StrMap = {
    "yyyy/mm/dd": Date2StrYYYYMMDD,
    "yyyy-mm-dd": Date2StrYYYYMMDD_Dash,
    "dd/mm/yyyy": Date2StrDDMMYYYY,
    "dd-mm-yyyy": Date2StrDDMMYYYY_Dash,
    "yyyy/mm/dd hh:mm:ss": Date2StrYYYYMMDDDateTime,
    "yyyy-mm-dd hh:mm:ss": Date2StrYYYYMMDDDateTime_Dash,
    "dd/mm/yyyy hh:mm:ss": Date2StrDDMMYYYYDateTime,
    "dd-mm-yyyy hh:mm:ss": Date2StrDDMMYYYYDateTime_Dash,
  };

  if (inputFormat && typeof date === "string") {
    result = str2DateMap[inputFormat](date);
  }

  if (outputFormat && result instanceof Date) {
    result = date2StrMap[outputFormat](result);
  }

  return result;
};

export { formatDate };

export const formatDateOrNow = (
  dateValue?: string | null,
  format = "YYYY-MM-DDTHH:mm:ss"
) => {
  const dateNow = dayjs().format(format);
  return dateValue ? dayjs.utc(dateValue).format(format) : dateNow;
};
